import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Upload, RefreshCw } from 'lucide-react';
import { Modal, Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import { AUTH_REDIRECT } from '../../utils/apiUtils';
import { OCR_ROLES } from '../../utils/roles';
import OcrReviewModal from '../OcrHub/OcrReviewModal';
import SingleOcrRegion from './SingleOcrRegion';
import SingleOcrTaskPreview from './SingleOcrTaskPreview';
import { clipboardOcrImage, validateOcrFile } from './singleOcrInput';
import { listSingleOcr, getSingleOcr, createSingleOcr, deleteSingleOcr, readSingleOcr, analyzeSingleOcr, saveSingleOcr } from './singleOcrApi';

const NO_ITEMS = [];
const INPUT = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50';
function errorText(error) {
  const reason = error?.data?.reason;
  let message = error?.data?.detail && typeof error.data.detail === 'string'
    ? `${error.message} ${error.data.detail}` : error?.message || '작업을 완료하지 못했습니다.';
  if (reason === 'busy') message = '이 문제의 다른 작업이 진행 중입니다. 잠시 뒤 상태를 확인해 주세요.';
  if (reason === 'no_answer') message = '정답을 확인하고 AI 분석을 완료한 뒤 문제를 저장해 주세요.';
  if (reason === 'blank_region' || reason === 'not_read') message = '선택한 영역에서 문제를 읽지 못했습니다. 영역을 확인한 뒤 다시 읽어 주세요.';
  if (reason === 'image_missing') message = '원본 이미지가 없습니다. 내 작업으로 돌아가 파일을 다시 올려 주세요.';
  return message;
}

export default function SingleOcrModal({ onClose, onSaved, onDirtyChange }) {
  const { getAccessToken, userType } = useAuth();
  const allowed = OCR_ROLES.includes(userType);
  const [view, setView] = useState('start');
  const [session, setSession] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);
  const alive = useRef(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [list, setList] = useState(null);
  const [listError, setListError] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [filter, setFilter] = useState('open');
  const [page, setPage] = useState(1);
  const listSeq = useRef(0);
  const inputRef = useRef(null);
  const contentRef = useRef(null);
  const pasteRef = useRef(null);
  const reviewDirty = useRef(false);
  const dirtyCallback = useRef(onDirtyChange); dirtyCallback.current = onDirtyChange;
  const reportDirty = useCallback((dirty) => { reviewDirty.current = dirty; dirtyCallback.current?.(dirty); }, []);

  useEffect(() => { alive.current = true; return () => { alive.current = false; listSeq.current += 1; dirtyCallback.current?.(false); }; }, []);
  useEffect(() => {
    if (view !== 'review') reportDirty(!!file || !!busy);
  }, [view, file, busy, reportDirty]);
  useEffect(() => {
    if (!file || file.name.toLowerCase().endsWith('.pdf')) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    const handler = (e) => { if (busyRef.current || file || reviewDirty.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [file]);

  const loadList = useCallback(async () => {
    if (!allowed) return;
    const seq = ++listSeq.current; setListLoading(true); setListError('');
    try {
      const data = await listSingleOcr({ state: filter, page }, getAccessToken);
      if (alive.current && seq === listSeq.current && data !== AUTH_REDIRECT) setList(data);
    } catch (e) { if (alive.current && seq === listSeq.current) setListError(errorText(e)); }
    finally { if (alive.current && seq === listSeq.current) setListLoading(false); }
  }, [allowed, filter, page, getAccessToken]);
  useEffect(() => { if (view === 'start') loadList(); }, [view, loadList]);

  const routeSession = (data) => {
    setSession(data);
    if (data.state === 'saved') {
      if (data.problem_id != null) { reportDirty(false); onSaved(data.problem_id); }
      else { setView('start'); setError('저장된 문제가 삭제되어 열 수 없습니다.'); }
    } else setView(data.has_body || data.problem?.body_payload || data.problem?.body_lexical ? 'review' : 'region');
  };
  // 동기 요청의 중복 실행을 렌더 전에 막는다. 실패한 요청을 자동으로 재실행하지 않는다.
  const run = async (label, action, { reconcile = false } = {}) => {
    if (busyRef.current || !allowed || !alive.current) return { ok: false };
    busyRef.current = true; setBusy(label); setError('');
    try { return await action(); }
    catch (e) {
      if (!alive.current) return { ok: false };
      const message = errorText(e); setError(message);
      if (e.data?.reason === 'saved') {
        if (e.data.problem_id != null) { reportDirty(false); onSaved(e.data.problem_id); }
        else if (session) {
          const data = await getSingleOcr(session.single_id, getAccessToken).catch(() => null);
          if (alive.current && data && data !== AUTH_REDIRECT) routeSession(data);
        }
      } else if (reconcile && e.uncertain && session) {
        const data = await getSingleOcr(session.single_id, getAccessToken).catch(() => null);
        if (alive.current && data && data !== AUTH_REDIRECT) {
          if (data.state === 'saved') routeSession(data);
          else { setSession(data); setRefreshKey((k) => k + 1); }
        }
      }
      return { ok: false, message };
    } finally { busyRef.current = false; if (alive.current) setBusy(''); }
  };
  const selectFile = (next) => {
    if (busyRef.current) return;
    const validation = validateOcrFile(next);
    if (validation) { setError(validation); return; }
    setFile(next); setError('');
  };
  const paste = (event) => {
    if (view !== 'start') return;
    const incoming = clipboardOcrImage(event.clipboardData);
    if (!incoming) return;
    event.preventDefault();
    if (busyRef.current) return;
    if (incoming.error) setError(incoming.error); else selectFile(incoming.file);
  };
  pasteRef.current = paste;
  useEffect(() => {
    if (!allowed || view !== 'start') return undefined;
    const handle = (event) => {
      const dialog = contentRef.current?.closest('[role="dialog"]');
      const dialogs = [...document.querySelectorAll('[role="dialog"]')];
      if (dialog && dialogs[dialogs.length - 1] === dialog) pasteRef.current?.(event);
    };
    // 모달이 열리자마자 초점이 제목의 닫기 버튼에 있어도 붙여넣을 수 있다.
    document.addEventListener('paste', handle);
    return () => document.removeEventListener('paste', handle);
  }, [allowed, view]);
  const close = () => {
    if (busyRef.current) return;
    if (file && !window.confirm('선택한 파일은 아직 올리지 않았습니다. 닫을까요?')) return;
    onClose();
  };
  const openItem = (item) => {
    if (file && !window.confirm('선택한 파일을 비우고 기존 작업을 열까요?')) return;
    return run('작업을 불러오는 중', async () => {
      const data = await getSingleOcr(item.single_id, getAccessToken);
      if (alive.current && data !== AUTH_REDIRECT) { setFile(null); setName(''); routeSession(data); }
    });
  };
  const upload = () => run('파일을 올리는 중', async () => {
    const data = await createSingleOcr(file, name, getAccessToken);
    if (!alive.current || data === AUTH_REDIRECT) return;
    setFile(null); setName(''); routeSession(data);
  });
  const remove = (item) => {
    if (!window.confirm(`‘${item.name}’ 작업을 버릴까요? 이 작업은 되돌릴 수 없습니다.`)) return;
    run('작업을 버리는 중', async () => {
      const result = await deleteSingleOcr(item.single_id, getAccessToken);
      if (alive.current && result !== AUTH_REDIRECT) {
        if (list?.items?.length === 1 && page > 1) setPage((p) => p - 1); else loadList();
      }
    });
  };

  if (!allowed) return null;
  if (view === 'review' && session) return <OcrReviewModal
    isOpen singleProblem workflow="single" problemId={session.problem?.ocr_prob_id ?? session.ocr_prob_id}
    projectId={session.single_id} singlePages={session.pages} projectName={session.name} projectKeywords={NO_ITEMS} items={NO_ITEMS}
    closed={session.state === 'saved'} refreshKey={refreshKey} externalBusy={!!busy} externalNotice={error || busy}
    reanalyzing={busy.startsWith('AI 분석')}
    onClose={onClose} onDirtyChange={reportDirty} onChanged={() => setError('')}
    onSelectRegion={() => run('원본을 불러오는 중', async () => {
      const data = await getSingleOcr(session.single_id, getAccessToken);
      if (alive.current && data !== AUTH_REDIRECT) {
        if (data.state === 'saved') routeSession(data);
        else { setSession(data); setView('region'); }
      }
    })}
    onBankSave={() => run('문제 저장 중', async () => {
      const data = await saveSingleOcr(session.single_id, getAccessToken);
      if (!alive.current || data === AUTH_REDIRECT) return { ok: false };
      if (data.problem_id == null) throw new Error('저장된 문제 번호를 받지 못했습니다. 내 작업에서 저장 상태를 확인해 주세요.');
      reportDirty(false); onSaved(data.problem_id); return { ok: true };
    }, { reconcile: true })}
    onReanalyze={(_, prompt) => run('AI 분석 중 · 보통 0.5~2분', async () => {
      const data = await analyzeSingleOcr(session.single_id, prompt, getAccessToken);
      if (!alive.current || data === AUTH_REDIRECT) return { ok: false };
      // 검수 모달이 성공·실패 모두 최신 상세를 조회한 뒤 저장 잠금을 해제한다.
      setSession(data); return { ok: true, message: 'AI 분석을 완료했습니다.' };
    }, { reconcile: true })}
  />;

  return <Modal isOpen onClose={close} title={view === 'region' ? `한 문제 OCR — ${session?.name || ''}` : '한 문제 OCR'}
    size={view === 'region' ? 'max' : 'xl'} fixedHeight={view === 'region'} maxHeight="92vh" closeOnEsc={!busy} closeOnBackdropClick={!busy}
    bodyClassName={view === 'region' ? 'flex min-h-0 flex-col' : ''}>
    <div ref={contentRef} className="flex min-h-0 flex-1 flex-col">
      {busy && <p role="status" className="mb-3 flex items-center gap-2 text-xs text-primary"><Loader2 className="size-4 animate-spin" aria-hidden="true" />{busy}</p>}
      {error && <p role="alert" className="mb-3 rounded-lg bg-red-50 p-3 text-xs text-danger">{error}</p>}
      {view === 'region' && session ? <SingleOcrRegion key={session.single_id} session={session} busy={!!busy}
        onBack={() => { setError(''); setView(session.has_body ? 'review' : 'start'); }}
        onRefresh={() => run('원본을 불러오는 중', async () => {
          const data = await getSingleOcr(session.single_id, getAccessToken);
          if (alive.current && data !== AUTH_REDIRECT) { if (data.state === 'saved') routeSession(data); else setSession(data); }
        })}
        onRead={(options) => run('본문 읽기 중 · 보통 1~3분', async () => {
          const data = await readSingleOcr(session.single_id, options, getAccessToken);
          if (!alive.current || data === AUTH_REDIRECT) return { ok: false };
          routeSession(data); setRefreshKey((k) => k + 1);
        }, { reconcile: true })}
      /> : <div className="grid gap-5 md:grid-cols-2">
        <div className="min-w-0 space-y-3">
          <label className="block text-xs font-medium text-gray-600">작업 이름 (선택)<input className={`${INPUT} mt-1`} maxLength={200} value={name} disabled={!!busy} onChange={(e) => setName(e.target.value)} placeholder="비우면 파일 이름을 사용합니다." /></label>
          <div tabIndex={0} aria-label="캡처 이미지 붙여넣기" className="rounded-2xl border border-dashed border-primary/50 bg-primary/5 p-5 text-center outline-none focus-visible:ring-2 focus-visible:ring-primary"
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files.length !== 1) setError('파일은 한 개씩 올려 주세요.'); else selectFile(e.dataTransfer.files[0]); }}>
            <ImagePlus className="mx-auto mb-3 size-7 text-primary" aria-hidden="true" />
            <p className="text-sm font-semibold">캡처 후 Ctrl/Cmd+V로 붙여넣기</p>
            <p className="mt-1 text-xs text-gray-500">파일을 끌어 놓거나 선택해도 됩니다.</p>
            <Button size="sm" variant="secondary" className="mt-3" onClick={() => inputRef.current?.click()} disabled={!!busy}>파일 선택</Button>
            <input ref={inputRef} type="file" aria-label="OCR 파일" className="sr-only" accept=".png,.jpg,.jpeg,.pdf" disabled={!!busy} onChange={(e) => { if (e.target.files?.[0]) selectFile(e.target.files[0]); e.target.value = ''; }} />
            <p className="mt-3 text-xs text-gray-500">PNG·JPG·JPEG·PDF<br />이미지 20MB 이하 · PDF 50쪽 이하</p>
          </div>
          {file && <div className="rounded-xl border p-3">
            {preview && <img src={preview} alt="붙여넣거나 선택한 이미지 미리보기" className="mb-2 max-h-48 w-full rounded object-contain" />}
            <p className="break-all text-xs text-gray-700">{file.name}</p>
            <div className="mt-3 flex justify-end gap-2"><Button size="xs" variant="secondary" disabled={!!busy} onClick={() => setFile(null)}>선택 취소</Button><Button size="sm" icon={<Upload className="size-3.5" />} disabled={!!busy} loading={!!busy} onClick={upload}>파일 올리기</Button></div>
          </div>}
          <p className="text-xs leading-relaxed text-gray-500">미리보기를 확인한 뒤 파일을 올리세요. 다음 화면에서 쪽·영역을 고르고 본문을 읽습니다. 읽기가 끝나면 검수 화면의 [AI 분석] 버튼으로 분석할 수 있습니다.</p>
        </div>
        <div className="min-w-0 md:border-l md:pl-5">
          <div className="mb-3 flex flex-wrap items-center gap-2"><h3 className="text-sm font-semibold">내 작업</h3><div className="ml-auto flex gap-1" role="group" aria-label="내 OCR 작업 상태">{[['open', '진행 중'], ['saved', '저장됨']].map(([key, label]) => <button key={key} type="button" disabled={!!busy} aria-pressed={filter === key} onClick={() => { setFilter(key); setPage(1); setList(null); }} className={`rounded-full px-2 py-1 text-xs ${filter === key ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'}`}>{label}</button>)}</div><button type="button" aria-label="내 작업 새로고침" disabled={listLoading || !!busy} onClick={loadList} className="rounded p-1 text-gray-500"><RefreshCw className={`size-3.5 ${listLoading ? 'animate-spin' : ''}`} /></button></div>
          {listError && <p role="alert" className="mb-2 text-xs text-danger">{listError}</p>}
          {listLoading && <p role="status" className="text-xs text-gray-500">내 작업을 불러오는 중입니다.</p>}
          {!listLoading && !listError && !list?.items?.length && <p className="py-8 text-center text-xs text-gray-400">{filter === 'open' ? '진행 중인 작업이 없습니다.' : '저장한 작업이 없습니다.'}</p>}
          <ul data-single-ocr-list className="max-h-96 divide-y overflow-y-auto">{(list?.items || []).map((item) => {
            const unavailable = !!busy || (item.state === 'saved' && item.problem_id == null);
            return <li key={item.single_id} className="flex items-start gap-3 py-3">
              <SingleOcrTaskPreview item={item} disabled={unavailable} onOpen={() => openItem(item)} />
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm font-medium">{item.name}</p>
                <p className="mt-1 text-xs text-gray-500">{item.state === 'saved' ? (item.problem_id != null ? `문제 ${item.problem_id}` : '저장된 문제 삭제됨') : item.analysis_status === 150 ? 'AI 분석 중' : item.analysis_status === 500 ? '분석 실패 · 다시 시도 가능' : item.has_body ? (item.has_answer ? '본문·정답 있음' : '본문 읽음 · 정답 확인 필요') : '읽기 전 · 쪽·영역 선택 필요'}</p>
                <div className="mt-2 flex flex-wrap justify-end gap-2"><Button size="xs" variant="secondary" disabled={unavailable} onClick={() => openItem(item)}>{item.state === 'saved' ? '문제 보기' : '이어 하기'}</Button>{item.state !== 'saved' && <button type="button" disabled={!!busy} onClick={() => remove(item)} className="rounded px-2 py-1 text-xs text-gray-400 hover:bg-red-50 hover:text-danger">버리기</button>}</div>
              </div>
            </li>;
          })}</ul>
          {list?.total_pages > 1 && <div className="mt-3 flex items-center justify-center gap-3 text-xs"><Button size="xs" variant="secondary" disabled={page <= 1 || listLoading || !!busy} onClick={() => { setList(null); setPage((p) => p - 1); }}>이전</Button><span>{page} / {list.total_pages}</span><Button size="xs" variant="secondary" disabled={page >= list.total_pages || listLoading || !!busy} onClick={() => { setList(null); setPage((p) => p + 1); }}>다음</Button></div>}
        </div>
      </div>}
    </div>
  </Modal>;
}
